import { json, redirect, type LoaderFunction, type MetaFunction } from "@remix-run/node";
import { captureException } from "@sentry/remix";
import { Link, isRouteErrorResponse, useLoaderData, useLocation, useNavigate, useRouteError, useSearchParams } from "@remix-run/react";
import { useEffect, useState, useRef, useMemo, Component, ErrorInfo, ReactNode } from "react";
import ApiServiceV2 from "~/lib/services/GeoSearchServices";
import PropertiesService from "~/lib/services/PropertiesService";
import { getCountryName } from "~/lib/helpers/constants";
import {
  PaginationItem, PaginationLink, PaginationEllipsis
} from "~/components/ui/custom-radix-components/pagination";
import convertCurrency from "~/lib/helpers/currencyConverter";
import DesktopLayout from "~/components/blocks/Geosearch/DesktopLayout";
import MobileLayout from "~/components/blocks/Geosearch/MobileLayout";
import { getDeviceInfo } from "~/lib/hooks/useViewportListener";
import AreaServices from "~/lib/services/AreaServices";
import { getConfig, getRegionConfigByCountryCodeRegion, regionConfigs } from "custom-config/region.configs";
import { recordSearch, collectImpressions } from "~/lib/Telemetry";
import { geoSearchMeta } from "~/utils/Meta/geoSearchMeta";
import {
  buildGeoSearchPinsQueryString,
  extractGlobalPriceTier,
  hasViewportBoundsInSearchParams,
  normalizeMapPinsData,
} from "~/lib/helpers/mapPinsHelper";
import {
  getSearchPlaceIdFromCookieHeader,
  persistSearchPlaceId,
} from "~/lib/helpers/searchPlaceContext";
import NotFoundPage from "~/components/blocks/NotFoundPage";
import { STATE_SLUG_TO_REGION } from "~/components/blocks/common/constants";
import {
  filterOfficeTypesToFilterOptions,
} from "~/components/blocks/Geosearch/constants";
import {
  buildCountryPropertyPagePath,
  buildCountrySearchPagePath,
  isOfficeTypeSlug,
  normalizeWorkspaceTypeToken,
  officeTypeSlugToValue,
  officeTypeValueToSlug,
  officeTypeValuesToSlugParam,
  parseCountryLandingSegment,
  resolveWorkspaceTypeParamFromUrl,
  resolveWorkspaceTypeTokensFromUrl,
  workspaceTypeParamToApiValues,
} from "~/components/blocks/Geosearch/locationGeosearch";
import { buildSearchPageHref } from "~/lib/helpers/StringHelper";



/**
 * Canonical path for this search layout only.
 * `/uk/search/england/london` → `/uk/england/london`
 * `/us/search/tx` → `/us/tx`
 * `/search/tx` (no country segment) → `/us/tx`
 */
function canonicalPathWithoutSearch(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const searchIndex = segments.findIndex((segment) => segment.toLowerCase() === "search");
  if (searchIndex === -1) return pathname.startsWith("/") ? pathname : `/${pathname}`;

  if (searchIndex === 0) {
    segments[0] = "us";
  } else {
    segments.splice(searchIndex, 1);
  }

  return segments.length ? `/${segments.join("/")}` : "/";
}

/** Country-only search: `/au/search` or `/au/search/enterprise-office-australia`. */
function countryOnlySearchContext(pathname: string): {
  country: string;
  officeTypeSlug?: string;
} | null {
  const segments = pathname.split("/").filter(Boolean);
  const searchIndex = segments.findIndex((segment) => segment.toLowerCase() === "search");
  if (searchIndex === -1) return null;

  const country = (searchIndex === 0 ? "us" : segments[searchIndex - 1] || "us").toLowerCase();
  const rest = segments.slice(searchIndex + 1);
  if (rest.length === 0) return { country };
  if (rest.length !== 1) return null;

  const landing = parseCountryLandingSegment(country, rest[0]);
  if (!landing) return null;
  return { country, officeTypeSlug: landing.officeTypeSlug };
}

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {

  if (!data || !location) return [];

  const pathname = location.pathname || "/";
  const tags = geoSearchMeta(data, pathname, location.search);
  const canonicalPath = canonicalPathWithoutSearch(pathname);
  if (canonicalPath === pathname) return tags;

  return tags.map((tag) => {
    if (
      tag &&
      typeof tag === "object" &&
      "rel" in tag &&
      tag.rel === "canonical" &&
      "href" in tag &&
      typeof tag.href === "string" &&
      pathname !== "/"
    ) {
      const queryIndex = tag.href.indexOf("?");
      const hrefPath = queryIndex === -1 ? tag.href : tag.href.slice(0, queryIndex);
      const hrefQuery = queryIndex === -1 ? "" : tag.href.slice(queryIndex);
      if (hrefPath.endsWith(pathname)) {
        return {
          ...tag,
          href: hrefPath.slice(0, -pathname.length) + canonicalPath + hrefQuery,
        };
      }
    }
    return tag;
  });
};

function getSelectedCurrencyFromCookie(cookieHeader: string | null): string | undefined {
  if (!cookieHeader) return undefined;
  // Cookie is set client-side by `useRegionConfig().updateSelectedCurrency()`
  // as `selectedCurrency=<ISO>`.
  const match = cookieHeader.match(/(?:^|;\s*)selectedCurrency=([^;]+)/);
  const raw = match?.[1];
  if (!raw) return undefined;
  const value = decodeURIComponent(raw).trim();
  return value ? value : undefined;
}

function parseQueryString(queryString: string): Record<string, string | number> {
  const params = new URLSearchParams(queryString);
  const result: Record<string, string | number> = {};

  params.forEach((value, key) => {
    // Convert numeric values to actual numbers
    result[key] = isNaN(Number(value)) ? value : Number(value);
  });

  return result;
}

// Below code is not using anywhere currently, keeping it for future reference
// function expandScientificNotation(numStr) {
//   if (!/e/i.test(numStr)) return numStr; // if not in scientific form
//   const num = Number(numStr);
//   if (Number.isFinite(num)) return num.toString();

//   // manually expand when too large for normal Number
//   const [base, exponent] = numStr.toLowerCase().split('e');
//   const exp = parseInt(exponent, 10);
//   const [integer, fraction = ''] = base.split('.');
//   const digits = integer + fraction;
//   const decimalIndex = integer.length + exp;

//   if (exp >= 0) return digits.padEnd(decimalIndex, '0');
//   else return '0.' + '0'.repeat(Math.abs(exp) - 1) + digits;
// }



const getRegionForStateSlug = (stateSlug: string | undefined): string | null =>
  STATE_SLUG_TO_REGION.get(stateSlug?.toLowerCase() ?? "") ?? null;

export const loader: LoaderFunction = async ({ params, request }) => {

  const { isMobile } = getDeviceInfo(request);
  const url = new URL(request.url);
  const searchParams = url.searchParams; // Get filters from the URL

  // `/uk/search/office-space-united-kingdom` and
  // `/uk/search/enterprise-office-united-kingdom` sit in the `$state` slot.
  // Neither is a state, so drop it before region checks and the geo-search API.
  const countryLanding =
    params.state && !params.city && !params.offieType
      ? parseCountryLandingSegment(params.country || "us", params.state)
      : null;
  const isCountryLandingSlug = !!countryLanding;

  // Enforce canonical region prefix when the `state` segment clearly belongs
  // to a specific country (e.g. `england` -> `/uk`, `nsw` -> `/au`). This
  // prevents mismatched URLs like `/zm/search/england/london` from being
  // indexed or served and sends a permanent redirect to the correct region.
  const canonicalRegion = isCountryLandingSlug
    ? null
    : getRegionForStateSlug(params.state);
  const currentRegion = params.country?.toLowerCase();
  if (canonicalRegion && currentRegion !== canonicalRegion) {
    const segments = ["", canonicalRegion, "search"];
    if (params.state) segments.push(params.state);
    if (params.city) segments.push(params.city);
    if (params.offieType) segments.push(params.offieType);
    const redirectPath = `${segments.join("/")}${url.search}`;
    throw redirect(redirectPath, 301);
  }
  const searchParamsString = searchParams.toString();
  const routeParams: Record<string, string> = { country: params.country || "us", region: params.country || "us", ...params };
  if (isCountryLandingSlug) {
    delete routeParams.state;
    if (countryLanding?.officeTypeSlug) {
      routeParams.offieType = countryLanding.officeTypeSlug;
    }
  }

  const cookieHeader = request.headers.get("Cookie");
  const selectedCurrency = getSelectedCurrencyFromCookie(cookieHeader);
  const regionCurrency = (
    getRegionConfigByCountryCodeRegion(params.country || "us")?.currency ?? "USD"
  ).toUpperCase();
  // Match root.tsx: ignore mismatched cookie currency on regional URLs.
  const fromCurrency =
    selectedCurrency &&
      selectedCurrency.toUpperCase() === regionCurrency
      ? selectedCurrency.toUpperCase()
      : regionCurrency;
  const newParams = parseQueryString(searchParamsString);
  const placeIdFromCookie = getSearchPlaceIdFromCookieHeader(cookieHeader);
  if (placeIdFromCookie && !newParams.placeId) {
    newParams.placeId = placeIdFromCookie;
  }

  const offieTypeSlug = (params.offieType || countryLanding?.officeTypeSlug)?.toLowerCase();
  if (!newParams.workspaceType && offieTypeSlug && isOfficeTypeSlug(offieTypeSlug)) {
    const officeTypeValue = officeTypeSlugToValue(offieTypeSlug);
    if (officeTypeValue) {
      newParams.workspaceType = officeTypeValue;
    }
  }

  const isFirstLoad = searchParams.get("firstPageLoad") !== "false";

  const priceKeys = ["minPrice", "maxPrice"];
  for (const key of priceKeys) {
    if (Object.prototype.hasOwnProperty.call(newParams, key)) {
      newParams[key] = convertCurrency(
        parseInt(newParams[key].toString()),
        fromCurrency,
        regionCurrency,
        true,
      );
    }
  }

  // Remove 'country' from query params to prevent overriding the route param ISO code
  // (Google Ads URLs may include country=Hong_Kong which breaks API calls)
  delete newParams.country;

  if (typeof newParams.workspaceType === "string" && newParams.workspaceType.trim()) {
    newParams.workspaceType = workspaceTypeParamToApiValues(newParams.workspaceType.trim());
  }

  const apiRouteParams = { ...routeParams };
  delete apiRouteParams.offieType;

  const queryString = Object.entries({ ...apiRouteParams, ...newParams })
    .map(([key, value]) => {
      if (key === 'country') {
        return `${key}=${getCountryName(String(routeParams.country))}`
      }
      return `${key}=${value}`
    })
    .join("&");
  let workspace = null
  if (newParams.workspaceId) {
    workspace = await AreaServices.getWorkspace(String(newParams.workspaceId)).catch(() => null);
  }

  const apiQueryString = `${queryString}${queryString.includes('firstPageLoad') ? '' : `&firstPageLoad=${isFirstLoad}`}&perPage=42`; // Merge filters with API call
  const geoSearchPinsQuery = buildGeoSearchPinsQueryString(apiQueryString);
  const cityParam = routeParams.city ? String(routeParams.city) : undefined;
  const stateParam = routeParams.state ? String(routeParams.state) : undefined;
  const regionParam = routeParams.region ? String(routeParams.region) : undefined;
  const hasMapBoundaryParams = hasViewportBoundsInSearchParams(searchParams);
  const filterParams = [
    "minPrice",
    "maxPrice",
    "minPeople",
    "maxPeople",
    "workspaceType",
    "amenities",
    "facilities",
    "promo",
    "workspaceId",
    "sortBy",
    "order",
  ];
  const hasAppliedFilters = filterParams.some((key) => {
    const value = searchParams.get(key);
    return value !== null && value !== "";
  });
  const shouldFetchFeaturedProperties =
    !hasMapBoundaryParams &&
    !hasAppliedFilters &&
    ((newParams?.latitude && newParams?.longitude) || cityParam);
  const featuredProperties = shouldFetchFeaturedProperties
    ? await ApiServiceV2.getFeaturedProperties(`featured=true&country=${getCountryName(String(routeParams.country))}${newParams?.latitude ? `&latitude=${newParams?.latitude}` : ''}${newParams?.longitude ? `&longitude=${newParams?.longitude}` : ''}${cityParam ? `&city=${cityParam.charAt(0).toUpperCase() + cityParam.slice(1)}` : ''}`).catch(() => [])
    : [];

  const [geoData, geoSearchPinsResponse] = await Promise.all([
    ApiServiceV2.geoSearch(apiQueryString).catch((error) => {
      console.error("GeoSearch loader API error:", error);
      return { data: [], totalListings: 0, autoReCalibratedData: [] };
    }),
    ApiServiceV2.geoSearch(geoSearchPinsQuery).catch(() => null),
  ]);
  const data = geoData;
  const mapPins = geoSearchPinsResponse ? normalizeMapPinsData(geoSearchPinsResponse) : [];
  const globalPriceTier = geoSearchPinsResponse
    ? extractGlobalPriceTier(geoSearchPinsResponse)
    : null;

  const totalListings = data?.totalListings ?? data?.totalProperties ?? 0;

  const propertyGeoSearchQuery = new URLSearchParams();
  propertyGeoSearchQuery.set("country", getCountryName(regionParam ? regionParam : "us"));
  if (cityParam) {
    propertyGeoSearchQuery.set("city", cityParam);
  }
  if (stateParam) {
    propertyGeoSearchQuery.set("state", stateParam);
  }
  if (newParams.latitude != null && newParams.latitude !== "") {
    propertyGeoSearchQuery.set("latitude", String(newParams.latitude));
  }
  if (newParams.longitude != null && newParams.longitude !== "") {
    propertyGeoSearchQuery.set("longitude", String(newParams.longitude));
  }
  if (newParams.zoom != null && newParams.zoom !== "") {
    propertyGeoSearchQuery.set("zoom", String(newParams.zoom));
  }
  propertyGeoSearchQuery.set("perPage", "42");

  const hasPropertyGeoSearchLocation =
    (propertyGeoSearchQuery.has("latitude") && propertyGeoSearchQuery.has("longitude")) ||
    propertyGeoSearchQuery.has("city") ||
    propertyGeoSearchQuery.has("state") ||
    propertyGeoSearchQuery.has("country");

  const nearbyPropertyListings =
    totalListings === 0 && hasPropertyGeoSearchLocation
      ? await PropertiesService.geoSearch(propertyGeoSearchQuery.toString()).catch((error) => {
        console.error("Nearby property geoSearch loader API error:", error);
        return { data: [], totalListings: 0, autoReCalibratedData: [] };
      })
      : null;

  const locationSegments: string[] = [];
  if (cityParam) {
    locationSegments.push(cityParam.charAt(0).toUpperCase() + cityParam.slice(1));
  }
  if (stateParam) {
    locationSegments.push(
      stateParam.length <= 3
        ? stateParam.toUpperCase()
        : stateParam.charAt(0).toUpperCase() + stateParam.slice(1).toLowerCase(),
    );
  }
  if (!stateParam && !cityParam) {
    locationSegments.push(getCountryName(regionParam ? regionParam : "us"));
  }
  if (regionParam && stateParam && !cityParam) {
    locationSegments.push(getCountryName(regionParam ? regionParam : "us"));
  }
  const locationText = locationSegments.join(", ");

  const region = params.country || "us";
  const regionData =
    getConfig(region as keyof typeof regionConfigs) ??
    getRegionConfigByCountryCodeRegion(region) ??
    undefined;

  return json({
    data,
    nearbyPropertyListings,
    locationText,
    params: routeParams,
    query: apiQueryString,
    workspace,
    isMobile,
    featuredProperties,
    mapPins,
    globalPriceTier,
    regionData,
  });

};


type LayoutErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
  resetKey?: string;
};

// Error Boundary for layout components
class LayoutErrorBoundary extends Component<
  LayoutErrorBoundaryProps,
  { hasError: boolean; error?: Error }
> {
  constructor(props: LayoutErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: undefined };
  }

  static getDerivedStateFromError(error: unknown) {
    return { hasError: true, error: error instanceof Error ? error : undefined };
  }

  componentDidUpdate(prevProps: LayoutErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: undefined });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Report to Sentry (production-only behavior is controlled by Sentry init).
    captureException(error, {
      tags: { boundary: "search-layout" },
      extra: { componentStack: errorInfo.componentStack },
    });
    this.setState({ error });
    console.error('Layout Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-red-600">Something went wrong. Please refresh the page.</p>
            <p>Error Message: {this.state.error?.message || ''}</p>
            <pre className="mt-2 whitespace-pre-wrap break-words text-left text-xs text-gray-700 max-w-[90vw]">
              {this.state.error?.stack || ""}
            </pre>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function SearchRouteErrorFallback({
  status = 500,
  region = "us",
  details,
}: {
  status?: number;
  region?: string;
  details?: unknown;
}) {
  const searchHomeLink = buildCountryPropertyPagePath(region);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center max-w-xl">
        <p className="text-sm uppercase tracking-[0.2em] text-gray-500">Search Error</p>
        <h1 className="mt-3 text-2xl font-semibold text-gray-900">We couldn't load these search results.</h1>
        <p className="mt-3 text-base text-gray-600">
          This is a route error with status {status}, which is different from the client-only layout boundary above.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <Link
            to={searchHomeLink}
            className="px-4 py-2 bg-black text-white rounded"
          >
            Back to Offices
          </Link>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 border border-gray-300 rounded"
          >
            Retry
          </button>
        </div>

        {details !== undefined ? (
          <div className="mt-6 text-left rounded border border-gray-200 bg-gray-50 p-4">
            <div className="text-sm font-medium text-gray-800">
              Technical details
            </div>
            <pre className="mt-3 whitespace-pre-wrap break-words text-xs text-gray-900">
              {typeof details === "string"
                ? details
                : JSON.stringify(details, null, 2)}
            </pre>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  const location = useLocation();
  const firstSegment = location.pathname.split("/").filter(Boolean)[0]?.toLowerCase();
  const region = firstSegment || "us";

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return <NotFoundPage region={region} />;
    }

    return (
      <SearchRouteErrorFallback
        status={error.status}
        region={region}
        details={{
          type: "RouteErrorResponse",
          status: error.status,
          statusText: error.statusText,
          data: error.data,
        }}
      />
    );
  }

  if (error instanceof Error) {
    captureException(error, { tags: { boundary: "search-route" } });
    return (
      <SearchRouteErrorFallback
        status={500}
        region={region}
        details={{
          type: "Error",
          name: error.name,
          message: error.message,
          stack: error.stack,
        }}
      />
    );
  }

  return (
    <SearchRouteErrorFallback
      status={500}
      region={region}
      details={{ type: "Unknown", value: error }}
    />
  );
}

export default function Index() {

  const data = useLoaderData<typeof loader>();
  const location = useLocation();


  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedSort, setSelectedSort] = useState({ label: "Best Match", value: "featured", order: "asc" });
  const [range, setRange] = useState<any>(null);
  const ITEMS_PER_PAGE = 42


  // Total pages - safely calculate with fallback
  const totalPages = data?.data?.totalPages ? data.data.totalPages :
    data?.data?.totalListings ? Math.ceil(data.data.totalListings / ITEMS_PER_PAGE) : 1;

  const [filterData, setFilterData] = useState<any>({
    price: [],
    deskRange: [],
    officeTypes: [],
    sale: null,
    amenities: [],
    facilities: [],
    workspaceId: null,
    sortBy: "featured",
    order: "asc"
  })

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageHref = (page: number) => buildSearchPageHref(location.pathname, searchParams, page);
  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    navigate(buildSearchPageHref(location.pathname, searchParams, newPage));
  };
  const [drawerUrl, setDrawerUrl] = useState('');

  useEffect(() => {
    if (!searchParams.has("placeId")) return;
    const legacyPlaceId = searchParams.get("placeId");
    if (legacyPlaceId && legacyPlaceId !== "reset") {
      persistSearchPlaceId(legacyPlaceId);
    }
    const params = new URLSearchParams(searchParams);
    params.delete("placeId");
    navigate(`?${params.toString()}`, { replace: true });
  }, [navigate, searchParams]);

  const callGeoSearch = (options: { [x: string]: string; }) => {
    setLoading(true)
    let params = new URLSearchParams(searchParams);
    let newFilterData = {
      price: [],
      deskRange: [],
      officeTypes: [],
      sale: null,
      amenities: [],
      facilities: [],
      workspaceId: null,
      sortBy: "featured",
      order: "asc",
      page: 1
    }

    if (options.reset) {
      params = new URLSearchParams()
      setFilterData(newFilterData)
      params.set("firstPageLoad", "true");
      const countrySearch = countryOnlySearchContext(location.pathname);
      navigate(
        countrySearch?.officeTypeSlug
          ? buildCountrySearchPagePath(countrySearch.country)
          : "?",
      );
    } else {
      const initialValue = {
        minPrice: params.get('minPrice'),
        maxPrice: params.get('maxPrice'),
        minPeople: params.get('minPeople'),
        maxPeople: params.get('maxPeople'),
        workspaceType: params.get('workspaceType'),
        officeTypeFilterTouched: params.get('officeTypeFilterTouched'),
        amenities: params.get('amenities'),
        facilities: params.get('facilities'),
        workspaceId: params.get('workspaceId'),
        promo: params.get('promo'),
        minLatitude: params.get('minLatitude'),
        maxLatitude: params.get('maxLatitude'),
        minLongitude: params.get('minLongitude'),
        maxLongitude: params.get('maxLongitude'),
        latitude: params.get('latitude'),
        longitude: params.get('longitude'),
        page: params.get('page'),
        sortBy: params.get('sortBy'),
        order: params.get('order'),
        priceTiers: params.get('priceTiers'),
        q: params.get('q'),
        text: params.get('text'),
      }

      Object.entries(initialValue).forEach(([key, value]) => {
        const optionValue = options[key];

        if (optionValue === 'reset' || optionValue === '') {
          params.delete(key);
          return;
        }

        if (optionValue != null) {
          if (key === "workspaceType" && optionValue !== "All") {
            const values = optionValue.split(",").filter(Boolean);
            params.set(key, officeTypeValuesToSlugParam(values));
          } else {
            params.set(key, optionValue);
          }
          return;
        }

        if (value != null) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });

      params.set("firstPageLoad", "false");

      if (initialValue.page) {
        setCurrentPage(parseInt(initialValue.page))
      }
      params.delete("placeId");

      const countrySearch = countryOnlySearchContext(location.pathname);
      let nextPath = location.pathname;
      if (countrySearch && Object.prototype.hasOwnProperty.call(options, "workspaceType")) {
        const workspaceType = options.workspaceType;
        const values =
          !workspaceType || workspaceType === "reset" || workspaceType === "All"
            ? []
            : workspaceType.split(",").filter(Boolean);
        const singleSlug =
          values.length === 1
            ? officeTypeValueToSlug(normalizeWorkspaceTypeToken(values[0]))
            : undefined;
        if (singleSlug) {
          nextPath = buildCountrySearchPagePath(countrySearch.country, singleSlug);
          params.delete("workspaceType");
        } else if (countrySearch.officeTypeSlug) {
          nextPath = buildCountrySearchPagePath(countrySearch.country);
          if (values.length === 0) params.delete("workspaceType");
        }
      }

      const query = params.toString();
      navigate(query ? `${nextPath}?${query}` : nextPath);
    }
  }

  // Duplicate function for Drawer component - updates state only, doesn't navigate immediately
  const callGeoSearchForDrawer = (options: { [x: string]: string; }) => {
    const defaultFilters = {
      price: [],
      deskRange: [],
      officeTypes: [],
      sale: null,
      amenities: [],
      facilities: [],
      workspaceId: null,
      sortBy: "featured",
      order: "asc"
    };

    if (options.reset) {
      setFilterData(defaultFilters);
      // Navigate immediately on reset to clear all filters
      const params = new URLSearchParams(searchParams);
      const baseParams = new URLSearchParams();
      // Keep essential params like latitude, longitude, etc.
      if (params.get('latitude')) baseParams.set('latitude', params.get('latitude')!);
      if (params.get('longitude')) baseParams.set('longitude', params.get('longitude')!);
      if (params.get('zoom')) baseParams.set('zoom', params.get('zoom')!);
      baseParams.set('firstPageLoad', 'true');
      const countrySearch = countryOnlySearchContext(location.pathname);
      const resetPath = countrySearch?.officeTypeSlug
        ? buildCountrySearchPagePath(countrySearch.country)
        : "";
      navigate(resetPath ? `${resetPath}?${baseParams.toString()}` : `?${baseParams.toString()}`);
    } else {
      if (options["workspaceType"] !== undefined) {
        const officeTypes =
          options["workspaceType"] === "All"
            ? []
            : filterOfficeTypesToFilterOptions(
              options["workspaceType"].split(",").filter(Boolean),
            );
        setFilterData({ ...filterData, officeTypes });
      } else if (options["amenities"] !== undefined) {
        const amenities = options["amenities"].split(",").filter(Boolean);
        setFilterData({ ...filterData, amenities });
      } else {
        setFilterData({ ...filterData, ...options });
      }
    }
  }

  const applyDrawerFilters = () => {
    const options: Record<string, string> = {};
    const setOption = (key: string, value: string | null | undefined, resetValue = "reset") => {
      if (value !== undefined && value !== null && value !== "") {
        options[key] = value;
      } else {
        options[key] = resetValue;
      }
    };

    setOption('minPeople', filterData.minPeople);
    setOption('maxPeople', filterData.maxPeople);
    setOption('minPrice', filterData.minPrice);
    setOption('maxPrice', filterData.maxPrice);

    if (Array.isArray(filterData.officeTypes) && filterData.officeTypes.length > 0) {
      options.workspaceType = officeTypeValuesToSlugParam(filterData.officeTypes);
      options.officeTypeFilterTouched = '1';
    } else {
      options.workspaceType = 'reset';
      options.officeTypeFilterTouched = '1';
    }

    if (Array.isArray(filterData.amenities) && filterData.amenities.length > 0) {
      options.amenities = filterData.amenities.join(',');
    } else {
      options.amenities = 'reset';
    }

    setOption('promo', filterData.promo);
    setOption('sale', filterData.sale);
    setOption('workspaceId', filterData.workspaceId);
    setOption('sortBy', filterData.sortBy ?? "featured", "featured");
    setOption('order', filterData.order ?? "asc", "asc");
    options.page = '1';

    callGeoSearch(options);
  };

  // Build URL from filterData for Drawer navigation
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    const setOrDelete = (key: string, value?: string | null, allowDelete = true) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, value);
        return;
      }
      if (allowDelete) {
        params.delete(key);
      }
    };

    setOrDelete('minPeople', filterData.minPeople);
    setOrDelete('maxPeople', filterData.maxPeople);
    setOrDelete('minPrice', filterData.minPrice);
    setOrDelete('maxPrice', filterData.maxPrice);

    if (Array.isArray(filterData.officeTypes) && filterData.officeTypes.length > 0) {
      params.set('workspaceType', officeTypeValuesToSlugParam(filterData.officeTypes));
    } else {
      params.delete('workspaceType');
    }

    if (Array.isArray(filterData.amenities) && filterData.amenities.length > 0) {
      params.set('amenities', filterData.amenities.join(','));
    } else {
      params.delete('amenities');
    }

    setOrDelete('promo', filterData.promo);
    setOrDelete('sale', filterData.sale);
    setOrDelete('workspaceId', filterData.workspaceId);
    setOrDelete('sortBy', filterData.sortBy ?? "featured", false);
    setOrDelete('order', filterData.order ?? "asc", false);

    params.set('firstPageLoad', 'false');

    setDrawerUrl(`?${params.toString()}`);
  }, [filterData, searchParams]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const parseNullableInt = (value: string | null) => {
      if (value === null || value === "") return null;
      const n = parseInt(value, 10);
      return Number.isNaN(n) ? null : n;
    };
    const newFilterData = {
      // Use `null` for missing bounds so RangeSlider doesn't treat it as an explicit 0.
      price: [parseNullableInt(params.get("minPrice")), parseNullableInt(params.get("maxPrice"))],
      deskRange: [parseNullableInt(params.get("minPeople")), parseNullableInt(params.get("maxPeople"))],
      minPeople: params.get("minPeople") || "",
      maxPeople: params.get("maxPeople") || "",
      minPrice: params.get("minPrice") || "",
      maxPrice: params.get("maxPrice") || "",
      officeTypes: filterOfficeTypesToFilterOptions(
        resolveWorkspaceTypeTokensFromUrl(
          params.get("workspaceType"),
          data?.params?.offieType,
        ),
      ),
      amenities: params.get("amenities")?.split(",") || [],
      facilities: params.get("facilities")?.split(",") || [],
      sale: params.get("promo"),
      workspaceId: params.get("workspaceId"),
      sortBy: params.get("sortBy") || "featured",
      order: params.get("order") || "asc",
    };
    const page = params.get('page')
    if (page) {
      setCurrentPage(parseInt(page))
    } else {
      setCurrentPage(1)
    }
    setFilterData((prev: any) => ({ ...prev, ...newFilterData }));

  }, [searchParams, data?.params?.offieType])

  const parsedQuery = useMemo(() => {
    const fromApiQuery = Object.fromEntries(
      Object.entries(parseQueryString(data.query)).map(([k, v]) => [k, String(v)]),
    );
    const workspaceType = resolveWorkspaceTypeParamFromUrl(
      searchParams.get("workspaceType"),
      data?.params?.offieType,
    );
    const officeTypeFilterTouched = searchParams.get("officeTypeFilterTouched");
    const country = searchParams.get("country") ?? data?.params?.country;
    const state = searchParams.get("state") ?? data?.params?.state;
    const city = searchParams.get("city") ?? data?.params?.city;
    return {
      ...fromApiQuery,
      ...(country != null ? { country: String(country) } : {}),
      ...(state != null ? { state: String(state) } : {}),
      ...(city != null ? { city: String(city) } : {}),
      ...(workspaceType != null ? { workspaceType } : {}),
      ...(officeTypeFilterTouched != null ? { officeTypeFilterTouched } : {}),
    };
  }, [
    data.query,
    data?.params?.country,
    data?.params?.state,
    data?.params?.city,
    data?.params?.offieType,
    searchParams,
  ]);

  useEffect(() => {
    const fetchRange = async () => {
      const cityParam = data?.params?.city ? String(data.params.city) : undefined;
      const stateParam = data?.params?.state ? String(data.params.state) : undefined;
      const params = new URLSearchParams(searchParams);
      const latitude = params.get('latitude');
      const longitude = params.get('longitude');
      const rangeQuery = `?country=${getCountryName(String(data?.params?.country || 'us'))}${latitude ? `&latitude=${latitude}` : ''}${longitude ? `&longitude=${longitude}` : ''}${stateParam ? `&state=${stateParam}` : ''}${cityParam ? `&city=${cityParam.charAt(0).toUpperCase() + cityParam.slice(1)}` : ''}`;
      const rangeData = await ApiServiceV2.getCountryMaxPrice(rangeQuery).catch(() => null);
      setRange(rangeData);
    };
    if (data?.params?.country) {
      fetchRange();
    }
  }, [data?.params?.country, data?.params?.city, data?.params?.state, searchParams])

  // useEffect(() => {
  //   const params = new URLSearchParams(searchParams);

  //   const initialValue = {
  //     minPrice: params.get('minPrice'),
  //     maxPrice: params.get('maxPrice'),
  //     minPeople: params.get('minPeople'),
  //     maxPeople: params.get('maxPeople'),
  //     workspaceType: params.get('workspaceType'),
  //     amenities: params.get('amenities'),
  //     facilities: params.get('facilities'),
  //     sale: params.get('promo'),
  //     workspaceId: params.get('workspaceId')
  //   }

  //   let data = {}

  //   if (initialValue.minPrice && initialValue.maxPrice) {

  //     data = ({ ...data, price: [parseInt(initialValue.minPrice), parseInt(initialValue.maxPrice)] })
  //   }
  //   if (initialValue.minPeople && initialValue.maxPeople) {
  //     data = ({ ...data, deskRange: [parseInt(initialValue.minPeople), parseInt(initialValue.maxPeople)] })
  //   }
  //   if (initialValue.workspaceType) {
  //     if (initialValue.workspaceType.includes(',')) {
  //       data = ({ ...data, officeTypes: initialValue.workspaceType.split(',') })
  //     } else {
  //       data = ({ ...data, officeTypes: [initialValue.workspaceType] })
  //     }
  //   }
  //   if (initialValue.amenities) {
  //     data = ({ ...data, amenities: initialValue.amenities.split(',') })
  //   }
  //   if (initialValue.facilities) {
  //     data = ({ ...data, facilities: initialValue.facilities.split(',') })
  //   }
  //   if (initialValue.sale) {
  //     data = ({ ...data, sale: initialValue.sale })
  //   }
  //   if (initialValue.workspaceId) {
  //     data = ({ ...data, workspaceId: initialValue.workspaceId })
  //   }
  //   const page = params.get('page')
  //   if (page) {
  //     setCurrentPage(parseInt(page))
  //   } else {
  //     setCurrentPage(1)
  //   }
  //   setFilterData({ ...filterData, ...data })

  // }, [])

  const renderPaginationItems = () => {
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, i) => (
        <PaginationItem className="cursor-pointer" key={i}>
          <PaginationLink
            to={pageHref(i + 1)}
            isActive={currentPage === i + 1}
            onClick={() => handlePageChange(i + 1)}
          >
            {i + 1}
          </PaginationLink>
        </PaginationItem>
      ));
    }

    const firstPage = (
      <PaginationItem key="first" className="cursor-pointer">
        <PaginationLink to={pageHref(1)} isActive={currentPage === 1} onClick={() => handlePageChange(1)}>
          1
        </PaginationLink>
      </PaginationItem>
    );

    const lastPage = (
      <PaginationItem key="last" className="cursor-pointer">
        <PaginationLink
          to={pageHref(totalPages)}
          isActive={currentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </PaginationLink>
      </PaginationItem>
    );

    if (currentPage === 1) {
      return (
        <>
          {firstPage}
          <PaginationItem className="cursor-pointer">
            <PaginationLink to={pageHref(2)} onClick={() => handlePageChange(2)}>2</PaginationLink>
          </PaginationItem>
          <PaginationItem><PaginationEllipsis /></PaginationItem>
          {lastPage}
        </>
      );
    }

    if (currentPage > totalPages - 2) {
      return (
        <>
          {firstPage}
          <PaginationItem><PaginationEllipsis /></PaginationItem>
          <PaginationItem className="cursor-pointer">
            <PaginationLink to={pageHref(totalPages - 2)} onClick={() => handlePageChange(totalPages - 2)}>
              {totalPages - 2}
            </PaginationLink>
          </PaginationItem>
          <PaginationItem className="cursor-pointer">
            <PaginationLink to={pageHref(totalPages - 1)} isActive={currentPage === totalPages - 1} onClick={() => handlePageChange(totalPages - 1)}>
              {totalPages - 1}
            </PaginationLink>
          </PaginationItem>
          {lastPage}
        </>
      );
    }

    return (
      <>

        <PaginationItem className="cursor-pointer">
          <PaginationLink to={pageHref(currentPage)} isActive onClick={() => handlePageChange(currentPage)}>
            {currentPage}
          </PaginationLink>
        </PaginationItem>
        <PaginationItem className="cursor-pointer">
          <PaginationLink to={pageHref(currentPage + 1)} onClick={() => handlePageChange(currentPage + 1)}>
            {currentPage + 1}
          </PaginationLink>
        </PaginationItem>
        <PaginationItem><PaginationEllipsis /></PaginationItem>
        {lastPage}
      </>
    );
  };

  // Track search telemetry
  const lastTelemetryRef = useRef<string>('');

  useEffect(() => {
    if (data?.data?.totalListings !== undefined && data?.data?.totalListings > 0) {
      // Create a stable key from search params to prevent duplicate calls
      const searchParamsKey = searchParams.toString();

      // Only send telemetry if search params have actually changed
      if (lastTelemetryRef.current === searchParamsKey) {
        return;
      }

      lastTelemetryRef.current = searchParamsKey;

      const searchData = {
        latitude: Number(searchParams.get('latitude')),
        longitude: Number(searchParams.get('longitude')),
        formatted_address: searchParams.get('location'),
        workspace_type: searchParams.get("workspaceType"),
        workspace_id: searchParams.get("workspaceId"),
        building_id: searchParams.get("buildingId"),
        property_id: searchParams.get("propertyId"),
        min_price: Number(searchParams.get("minPrice")),
        max_price: Number(searchParams.get("maxPrice")),
        min_people: Number(searchParams.get("minPeople")),
        max_people: Number(searchParams.get("maxPeople")),
        order: searchParams.get("order"),
        promo: searchParams.get("promo"),
        zoom: Number(searchParams.get("zoom")),
        radius: Number(searchParams.get("radius")),
        page: Number(searchParams.get("page")) || currentPage,
        per_page: Number(searchParams.get("perPage")) || ITEMS_PER_PAGE
      };
      recordSearch(searchData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data?.totalListings, searchParams, currentPage]);

  // Collect impressions after listings are rendered - use requestIdleCallback for better performance
  useEffect(() => {
    if (data?.data?.totalListings !== undefined && data?.data?.totalListings > 0) {
      // Use requestIdleCallback to defer non-critical impression collection
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
          collectImpressions();
        }, { timeout: 2000 });
      } else {
        // Fallback for browsers that don't support requestIdleCallback
        setTimeout(() => {
          collectImpressions();
        }, 1500);
      }
    }
  }, [data?.data?.totalListings]);


  // Ensure data structure is safe before rendering
  if (!data || !data.data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <LayoutErrorBoundary resetKey={`${location.pathname}${location.search}`}>
      <div className="min-h-[calc(100vh-136px)]">
        {!data?.isMobile ? (
          <DesktopLayout
            query={data.query}
            workspace={data.workspace}
            filterData={filterData}
            callGeoSearch={callGeoSearch}
            callGeoSearchForDrawer={callGeoSearchForDrawer}
            setFilterData={setFilterData}
            data={data}
            currentPage={currentPage}
            selectedSort={selectedSort}
            setSelectedSort={setSelectedSort}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            renderPaginationItems={renderPaginationItems}
            featuredProperties={data.featuredProperties}
            maxPrice={range}
            mobile={false}
            drawerUrl={drawerUrl}
            onApplyDrawerFilters={applyDrawerFilters}
            parsedQuery={parsedQuery}
            isProperty={false}
            areaSummary={null}
            pageSize={ITEMS_PER_PAGE}
          />
        ) : (
          <MobileLayout
            query={data.query}
            workspace={data.workspace}
            filterData={filterData}
            callGeoSearch={callGeoSearch}
            callGeoSearchForDrawer={callGeoSearchForDrawer}
            setFilterData={setFilterData}
            data={data}
            currentPage={currentPage}
            selectedSort={selectedSort}
            setSelectedSort={setSelectedSort}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            renderPaginationItems={renderPaginationItems}
            featuredProperties={data.featuredProperties}
            maxPrice={range}
            drawerUrl={drawerUrl}
            onApplyDrawerFilters={applyDrawerFilters}
            parsedQuery={parsedQuery}
            areaSummary={null}
            pageSize={ITEMS_PER_PAGE}
          />
        )}
      </div>
    </LayoutErrorBoundary>
  );
}

